// All user-facing widget copy, in both storefront languages. The active
// locale is resolved once at init (initWidgetLocale, called from embed.ts
// before any DOM is built) from an explicit config override or the page's
// own <html lang> — Shopify sets that per storefront locale, so a bilingual
// store gets the matching language automatically.
//
// Deliberately not a general i18n framework: two locales, a flat key set,
// and a single {placeholder} substitution helper are all this widget needs.

export type WidgetLocale = 'en' | 'ar';

type StringKey =
  | 'launcherOpen'
  | 'launcherClose'
  | 'panelAria'
  | 'headerTitle'
  | 'statusConnecting'
  | 'statusConnected'
  | 'statusReconnecting'
  | 'beingHelpedBy'
  | 'moreOptions'
  | 'endChat'
  | 'endChatAria'
  | 'soundToggleLabel'
  | 'unreadBadgeAria'
  | 'notificationsToggleLabel'
  | 'notificationsBlockedLabel'
  | 'notificationTitle'
  | 'notificationReplyBody'
  | 'notificationAttachmentBody'
  | 'notificationAggregateBody'
  | 'notificationSupportFallbackName'
  | 'teaserAria'
  | 'teaserDismiss'
  | 'teaserOnline'
  | 'teaserNewVisitor'
  | 'teaserWelcomeBack'
  | 'teaserOrder'
  | 'teaserUnread'
  | 'staffTyping'
  | 'teaserClosed'
  | 'dropToAttach'
  | 'dropHint'
  | 'messageActions'
  | 'reply'
  | 'copyText'
  | 'copied'
  | 'report'
  | 'reported'
  | 'reportFailed'
  | 'replyingTo'
  | 'replyingToYou'
  | 'replyingToSupport'
  | 'automatedReply'
  | 'cancelReply'
  | 'addReaction'
  | 'homePrompt'
  | 'conversationEnded'
  | 'startNewChat'
  | 'emptyTitle'
  | 'emptyDescription'
  | 'unableToStart'
  | 'failedToLoadMessages'
  | 'failedToLoadConversation'
  | 'tryAgain'
  | 'failedToSend'
  | 'typeMessage'
  | 'messageAria'
  | 'chooseEmoji'
  | 'attachFile'
  | 'sendMessage'
  | 'poweredByPrefix'
  | 'uploading'
  | 'uploadFailed'
  | 'unsupportedFileType'
  | 'fileTooLarge'
  | 'retryUpload'
  | 'removeAttachment'
  | 'sent'
  | 'deliverySending'
  | 'deliveryDelivered'
  | 'deliveryFailed'
  | 'deliveryReview'
  | 'resendMessage'
  | 'carryOverTitle'
  | 'carryOverFiles'
  | 'dividerToday'
  | 'staffInitial'
  | 'recentConversationsTitle'
  | 'conversationFallbackTitle'
  | 'historyUnreadAria'
  | 'viewingPast'
  | 'back'
  | 'statusOpen'
  | 'statusClosed'
  | 'relToday'
  | 'relYesterday'
  | 'relDaysAgo'
  | 'viewImage'
  | 'downloadFile'
  | 'closeImage'
  | 'loadingImage'
  | 'retryLoadingImage'
  | 'loadingVideo'
  | 'retryLoadingVideo'
  | 'loginTitle'
  | 'loginDescription'
  | 'loginAction'
  | 'registerAction'
  | 'linkMissingTitle'
  | 'linkMissingDescription'
  | 'linkMissingAction'
  | 'linkMissingRetry'
  | 'greeting'
  | 'nameRequiredTitle'
  | 'nameRequiredDescription'
  | 'nameInputPlaceholder'
  | 'nameInputAria'
  | 'nameSaveAction'
  | 'nameRequiredError'
  | 'nameSaveFailed'
  | 'staffJoined';

const STRINGS: Record<WidgetLocale, Record<StringKey, string>> = {
  en: {
    launcherOpen: 'Open support chat',
    launcherClose: 'Close support chat',
    panelAria: 'Support chat',
    headerTitle: 'Flix Support',
    statusConnecting: 'Connecting…',
    statusConnected: 'We typically reply within a few minutes',
    statusReconnecting: 'Reconnecting…',
    beingHelpedBy: '{name} • Support Team',
    moreOptions: 'More options',
    endChat: 'End chat',
    endChatAria: 'End this conversation',
    soundToggleLabel: 'Notification sounds',
    unreadBadgeAria: '{count} unread messages',
    notificationsToggleLabel: 'Browser notifications',
    notificationsBlockedLabel: 'Browser notifications (blocked — check your browser settings)',
    notificationTitle: 'Flix Support',
    notificationReplyBody: '{name} replied:\n"{snippet}"',
    notificationAttachmentBody: '{name} sent an attachment.',
    notificationAggregateBody: '{count} new replies from Support.',
    notificationSupportFallbackName: 'Support',
    teaserAria: 'Message from Flix Support',
    teaserDismiss: 'Dismiss',
    teaserOnline: 'Online',
    teaserNewVisitor: '👋 Welcome to Flix Store!\nNeed help choosing the right service?',
    teaserWelcomeBack: '👋 Welcome back, {name}\nOur support team is online.',
    teaserOrder: '📦 Need an update on your order?\nOur team is ready to help.',
    teaserUnread: '🔔 You have a new reply from Support.',
    staffTyping: '{name} is typing',
    teaserClosed: '✅ Conversation closed.\nNeed anything else? Start a new conversation anytime.',
    dropToAttach: 'Drop files to attach',
    dropHint: 'Images, videos, PDFs · up to 50 MB',
    messageActions: 'Message actions',
    reply: 'Reply',
    copyText: 'Copy text',
    copied: 'Copied',
    report: 'Report',
    reported: 'Reported to our team',
    reportFailed: "Couldn't report this message.",
    replyingTo: 'Replying to {name}',
    replyingToYou: 'Replying to you',
    replyingToSupport: 'Replying to Support',
    automatedReply: 'Automated reply',
    cancelReply: 'Cancel reply',
    addReaction: 'React with {emoji}',
    homePrompt: 'Chat with our support team.',
    conversationEnded: 'This conversation has ended.',
    startNewChat: 'Start new chat',
    emptyTitle: 'Start a conversation',
    emptyDescription: 'Ask us anything — we typically reply within a couple of minutes.',
    unableToStart: 'Unable to start the conversation.',
    failedToLoadMessages: 'Failed to load messages.',
    failedToLoadConversation: 'Failed to load this conversation.',
    tryAgain: 'Try again',
    failedToSend: 'Failed to send message. Please try again.',
    typeMessage: 'Type your message…',
    messageAria: 'Message',
    chooseEmoji: 'Choose an emoji',
    attachFile: 'Attach a file',
    sendMessage: 'Send message',
    poweredByPrefix: 'Powered by ',
    uploading: 'Uploading…',
    uploadFailed: 'Upload failed.',
    unsupportedFileType: "This file type isn't supported. Please send an image, a video, or a PDF.",
    fileTooLarge: 'File is too large. The maximum size is 50 MB.',
    retryUpload: 'Retry uploading {name}',
    removeAttachment: 'Remove {name}',
    sent: 'Sent',
    deliverySending: 'Sending to support',
    deliveryDelivered: 'Delivered to support',
    deliveryFailed: 'Not delivered to support — try sending it again',
    deliveryReview: 'Checking it reached support',
    resendMessage: 'Resend',
    carryOverTitle: "Messages from your previous chat that didn't reach support",
    carryOverFiles: '📎 {count}',
    dividerToday: 'TODAY',
    staffInitial: 'S',
    recentConversationsTitle: 'Recent conversations',
    historyUnreadAria: 'Unread reply',
    conversationFallbackTitle: 'Conversation',
    viewingPast: 'Viewing a past conversation',
    back: 'Back',
    statusOpen: 'Open',
    statusClosed: 'Closed',
    relToday: 'Today',
    relYesterday: 'Yesterday',
    relDaysAgo: '{n} days ago',
    viewImage: 'View image {name}',
    downloadFile: 'Download {name}',
    closeImage: 'Close image',
    loadingImage: 'Loading image {name}',
    retryLoadingImage: 'Retry loading image {name}',
    loadingVideo: 'Loading video {name}',
    retryLoadingVideo: 'Retry loading video {name}',
    loginTitle: 'Login required',
    loginDescription:
      'Please log in or create an account to chat with our support team. This lets us verify who you are and link the conversation to your account for faster, safer support.',
    loginAction: 'Log in',
    registerAction: 'Create account',
    linkMissingTitle: 'Link your account to use support',
    linkMissingDescription:
      "You're signed in, but your Flix account isn't linked to a store customer profile yet, so we can't open a verified support conversation. Verify your email and claim your previous purchases from your account page, then try again.",
    linkMissingAction: 'Open my account',
    linkMissingRetry: 'Try again',
    greeting: 'Hi, {name} 👋',
    nameRequiredTitle: "What's your name?",
    nameRequiredDescription:
      "So our support team knows who they're talking to. You'll only need to do this once.",
    nameInputPlaceholder: 'Your name',
    nameInputAria: 'Your name',
    nameSaveAction: 'Continue',
    nameRequiredError: 'Please enter your name to continue.',
    nameSaveFailed: "Couldn't save your name. Please try again.",
    staffJoined: '{name} is now handling your request.',
  },
  ar: {
    launcherOpen: 'فتح دردشة الدعم',
    launcherClose: 'إغلاق دردشة الدعم',
    panelAria: 'دردشة الدعم',
    headerTitle: 'دعم Flix',
    statusConnecting: 'جارٍ الاتصال…',
    statusConnected: 'عادةً ما نرد خلال دقائق قليلة',
    statusReconnecting: 'جارٍ إعادة الاتصال…',
    beingHelpedBy: '{name} • فريق الدعم',
    moreOptions: 'خيارات إضافية',
    endChat: 'إنهاء المحادثة',
    endChatAria: 'إنهاء هذه المحادثة',
    soundToggleLabel: 'أصوات الإشعارات',
    unreadBadgeAria: '{count} رسائل غير مقروءة',
    notificationsToggleLabel: 'إشعارات المتصفح',
    notificationsBlockedLabel: 'إشعارات المتصفح (محظورة — تحقق من إعدادات متصفحك)',
    notificationTitle: 'Flix Support',
    notificationReplyBody: '{name} رد:\n"{snippet}"',
    notificationAttachmentBody: '{name} أرسل مرفقًا.',
    notificationAggregateBody: '{count} ردود جديدة من فريق الدعم.',
    notificationSupportFallbackName: 'فريق الدعم',
    teaserAria: 'رسالة من فريق دعم Flix',
    teaserDismiss: 'إغلاق',
    teaserOnline: 'متصل',
    teaserNewVisitor: '👋 أهلًا بك في Flix Store!\nتحتاج مساعدة في اختيار الخدمة المناسبة؟',
    teaserWelcomeBack: '👋 أهلًا بعودتك، {name}\nفريق الدعم متصل الآن.',
    teaserOrder: '📦 تحتاج تحديثًا عن طلبك؟\nفريقنا جاهز لمساعدتك.',
    teaserUnread: '🔔 لديك رد جديد من فريق الدعم.',
    staffTyping: '{name} بيكتب الآن',
    teaserClosed: '✅ تم إغلاق المحادثة.\nتحتاج أي شيء آخر؟ يمكنك بدء محادثة جديدة في أي وقت.',
    dropToAttach: 'أفلت الملفات لإرفاقها',
    dropHint: 'صور وفيديوهات وملفات PDF · حتى 50 ميجابايت',
    messageActions: 'إجراءات الرسالة',
    reply: 'رد',
    copyText: 'نسخ النص',
    copied: 'تم النسخ',
    report: 'إبلاغ',
    reported: 'تم الإبلاغ لفريقنا',
    reportFailed: 'تعذّر الإبلاغ عن هذه الرسالة.',
    replyingTo: 'الرد على {name}',
    replyingToYou: 'الرد عليك',
    replyingToSupport: 'الرد على الدعم',
    automatedReply: 'رد آلي',
    cancelReply: 'إلغاء الرد',
    addReaction: 'تفاعل بـ {emoji}',
    homePrompt: 'تواصل مع فريق الدعم.',
    conversationEnded: 'انتهت هذه المحادثة.',
    startNewChat: 'بدء محادثة جديدة',
    emptyTitle: 'ابدأ محادثة',
    emptyDescription: 'اسألنا أي شيء — عادةً ما نرد خلال دقائق قليلة.',
    unableToStart: 'تعذّر بدء المحادثة.',
    failedToLoadMessages: 'تعذّر تحميل الرسائل.',
    failedToLoadConversation: 'تعذّر تحميل هذه المحادثة.',
    tryAgain: 'إعادة المحاولة',
    failedToSend: 'تعذّر إرسال الرسالة. حاول مرة أخرى.',
    typeMessage: 'اكتب رسالتك…',
    messageAria: 'الرسالة',
    chooseEmoji: 'اختر رمزًا تعبيريًا',
    attachFile: 'إرفاق ملف',
    sendMessage: 'إرسال الرسالة',
    poweredByPrefix: 'مدعوم من ',
    uploading: 'جارٍ الرفع…',
    uploadFailed: 'فشل الرفع.',
    unsupportedFileType: 'هذا النوع من الملفات غير مدعوم. الرجاء إرسال صورة أو فيديو أو ملف PDF.',
    fileTooLarge: 'الملف كبير جدًا. الحد الأقصى للحجم هو 50 ميجابايت.',
    retryUpload: 'إعادة رفع {name}',
    removeAttachment: 'إزالة {name}',
    sent: 'تم الإرسال',
    deliverySending: 'جارٍ الإرسال للدعم',
    deliveryDelivered: 'وصلت للدعم',
    deliveryFailed: 'لم تصل للدعم — حاول إرسالها مرة أخرى',
    deliveryReview: 'قيد التحقق من وصولها للدعم',
    resendMessage: 'إعادة الإرسال',
    carryOverTitle: 'رسائل لم تصل للدعم من محادثتك السابقة',
    carryOverFiles: '📎 {count}',
    dividerToday: 'اليوم',
    staffInitial: 'د',
    recentConversationsTitle: 'المحادثات السابقة',
    historyUnreadAria: 'رد غير مقروء',
    conversationFallbackTitle: 'محادثة',
    viewingPast: 'أنت تعرض محادثة سابقة',
    back: 'رجوع',
    statusOpen: 'مفتوحة',
    statusClosed: 'مغلقة',
    relToday: 'اليوم',
    relYesterday: 'أمس',
    relDaysAgo: 'منذ {n} أيام',
    viewImage: 'عرض الصورة {name}',
    downloadFile: 'تنزيل {name}',
    closeImage: 'إغلاق الصورة',
    loadingImage: 'جارٍ تحميل الصورة {name}',
    retryLoadingImage: 'إعادة محاولة تحميل الصورة {name}',
    loadingVideo: 'جارٍ تحميل الفيديو {name}',
    retryLoadingVideo: 'إعادة محاولة تحميل الفيديو {name}',
    loginTitle: 'تسجيل الدخول مطلوب',
    loginDescription:
      'يرجى تسجيل الدخول أو إنشاء حساب للتواصل مع فريق الدعم. يساعدنا ذلك على التحقق من هويتك وربط المحادثة بحسابك لتقديم دعم أسرع وأكثر أمانًا.',
    loginAction: 'تسجيل الدخول',
    registerAction: 'إنشاء حساب',
    linkMissingTitle: 'اربط حسابك لاستخدام الدعم',
    linkMissingDescription:
      'تم تسجيل دخولك، لكن حسابك في فليكس غير مربوط بعدُ بملف عميل في المتجر، لذا لا يمكننا فتح محادثة دعم موثّقة. وثّق بريدك الإلكتروني واربط مشترياتك السابقة من صفحة حسابك، ثم أعد المحاولة.',
    linkMissingAction: 'فتح حسابي',
    linkMissingRetry: 'إعادة المحاولة',
    greeting: 'مرحبًا، {name} 👋',
    nameRequiredTitle: 'ما اسمك؟',
    nameRequiredDescription: 'عشان فريق الدعم يعرف يتعامل مع مين. هنسألك مرة واحدة بس.',
    nameInputPlaceholder: 'اسمك',
    nameInputAria: 'اسمك',
    nameSaveAction: 'متابعة',
    nameRequiredError: 'من فضلك اكتب اسمك للمتابعة.',
    nameSaveFailed: 'تعذّر حفظ اسمك. حاول مرة أخرى.',
    staffJoined: '{name} يتابع طلبك الآن.',
  },
};

let activeLocale: WidgetLocale | null = null;

function detectLocaleFromPage(): WidgetLocale {
  return document.documentElement.lang.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

// Called once from embed.ts before any widget DOM is built. Calling it
// again (e.g. FlixChat.destroy() + init() on a locale-switched SPA page)
// re-resolves cleanly.
export function initWidgetLocale(override?: WidgetLocale): void {
  activeLocale = override ?? detectLocaleFromPage();
}

export function getWidgetLocale(): WidgetLocale {
  // Lazy fallback so direct createWidget() callers (tests, programmatic
  // embeds) that never go through embed.ts still get a sensible locale.
  if (activeLocale === null) {
    activeLocale = detectLocaleFromPage();
  }

  return activeLocale;
}

export function getWidgetDir(): 'ltr' | 'rtl' {
  return getWidgetLocale() === 'ar' ? 'rtl' : 'ltr';
}

export function t(key: StringKey): string {
  return STRINGS[getWidgetLocale()][key];
}

// The one substitution shape this widget needs: t('key') with a single
// named {placeholder}.
export function tf(key: StringKey, params: Record<string, string | number>): string {
  let result = t(key);

  for (const [name, value] of Object.entries(params)) {
    result = result.replace(`{${name}}`, String(value));
  }

  return result;
}
